<template>
  <div class="welcome-message-container">
    <!-- 文本展示模式 -->
    <div v-if="!isEditMode" class="message-display-wrapper">
      <div class="message-display">
        <span>{{ beforeText }}</span>
        <span class="display-nickname">{{ $t('lang.k_269001') }}</span>
        <span>{{ afterText }}</span>
      </div>
      <button type="text" plain class="edit-button" @click="enterEditMode">
        {{ $t('lang.k_269002') }}
      </button>
    </div>

    <!-- 编辑模式 -->
    <div v-else class="edit-mode-container">
      <div class="message-input-wrapper">
        <!-- 使用三个独立的可编辑区域来确保昵称标签不被删除 -->
        <div class="message-container">
          <div
            class="before-nickname"
            contenteditable="true"
            @input="handleBeforeInput"
            @keydown="handleBeforeKeyDown"
          ></div>
          <span class="nickname-tag" ref="nicknameTag">{{ $t('lang.k_269003') }}</span>
          <div
            class="after-nickname"
            contenteditable="true"
            @input="handleAfterInput"
            @keydown="handleAfterKeyDown"
          ></div>
        </div>
        <span :class="['char-count', { error: !canSave }]">{{ characterCount }}/10</span>
      </div>
      <button type="text" plain class="save-button" @click="saveMessage">
        {{ $t('lang.k_269004') }}
      </button>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'WelcomeMessage',
    props: {
      message: {
        type: String,
        default() {
          return this.$t('lang.k_269005');
        }
      }
    },
    data() {
      return {
        maxLength: 10,
        nicknameText: this.$t('lang.k_269003'),
        characterCount: 0,
        beforeText: '',
        afterText: '',
        isEditMode: false // 控制是否处于编辑模式
      };
    },
    computed: {
      canSave() {
        // 只要字符数在限制内就可以保存
        return this.characterCount <= this.maxLength;
      }
    },
    mounted() {
      // 从props中解析message，提取昵称前后的文本
      this.parseMessageProp();

      // 只在编辑模式下执行长度限制和字符计数初始化
      if (this.isEditMode) {
        this.$nextTick(() => {
          this.enforceCharacterLimit('both');
          this.updateCharacterCount();
        });
      } else {
        // 非编辑模式下直接计算字符数
        this.characterCount = this.beforeText.length + this.afterText.length;
      }
    },
    watch: {
      // 监听message属性变化，重新解析
      message: {
        handler() {
          this.parseMessageProp();
        },
        immediate: false
      }
    },
    methods: {
      // 进入编辑模式
      enterEditMode() {
        this.isEditMode = true;
        // 确保编辑区域的内容与数据同步
        this.$nextTick(() => {
          if (this.$el) {
            const beforeEl = this.$el.querySelector('.before-nickname');
            const afterEl = this.$el.querySelector('.after-nickname');
            if (beforeEl) beforeEl.textContent = this.beforeText;
            if (afterEl) afterEl.textContent = this.afterText;

            // 更新字符计数
            this.updateCharacterCount();
          }
        });
      },

      // 解析message属性，提取昵称前后的文本
      parseMessageProp() {
        const message = this.message || this.$t('lang.k_269005');
        console.log('message:', message);
        const nicknamePlaceholder = '{user_nickname}';
        const nicknameIndex = message.indexOf(nicknamePlaceholder);
        console.log('message nicknameIndex:', nicknameIndex);
        if (nicknameIndex !== -1) {
          // 提取昵称前的文本
          this.beforeText = message.substring(0, nicknameIndex);
          console.log('message beforeText:', this.beforeText);
          // 提取昵称后的文本
          this.afterText = message.substring(nicknameIndex + nicknamePlaceholder.length);
          console.log('message afterText:', this.afterText);
        } else {
          // 如果没有找到昵称占位符，默认使用前后文本
          this.beforeText = this.$t('lang.k_269006');
          this.afterText = this.$t('lang.k_269007');
        }

        // 更新DOM中的文本内容
        if (this.$el && this.isEditMode) {
          const beforeEl = this.$el.querySelector('.before-nickname');
          const afterEl = this.$el.querySelector('.after-nickname');
          if (beforeEl) beforeEl.textContent = this.beforeText;
          if (afterEl) afterEl.textContent = this.afterText;
        }
      },

      // 更新字符计数
      updateCharacterCount() {
        // 根据当前模式获取文本内容
        if (this.isEditMode && this.$el) {
          const beforeText = this.$el.querySelector('.before-nickname')?.textContent || '';
          const afterText = this.$el.querySelector('.after-nickname')?.textContent || '';
          this.beforeText = beforeText;
          this.afterText = afterText;
          this.characterCount = beforeText.length + afterText.length;
        } else {
          // 非编辑模式下直接使用数据模型
          this.characterCount = this.beforeText.length + this.afterText.length;
        }
      },
      handleBeforeInput() {
        this.enforceCharacterLimit('before');
        this.updateCharacterCount();
      },
      handleAfterInput() {
        this.enforceCharacterLimit('after');
        this.updateCharacterCount();
      },
      handleBeforeKeyDown(event) {
        // 处理特殊按键
        this.handleSpecialKeys(event, 'before');
      },
      handleAfterKeyDown(event) {
        // 处理特殊按键
        this.handleSpecialKeys(event, 'after');
      },
      handleSpecialKeys(event, position) {
        const beforeEl = this.$el.querySelector('.before-nickname');
        const afterEl = this.$el.querySelector('.after-nickname');
        const nicknameEl = this.$el.querySelector('.nickname-tag');

        const selection = window.getSelection();
        const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

        if (!range) return;

        // 处理方向键
        if (event.key === 'ArrowLeft' && position === 'before') {
          // 如果光标在before区域的最左侧，阻止继续向左移动
          const isAtStart = range.startContainer === beforeEl.firstChild && range.startOffset === 0;
          if (isAtStart || !beforeEl.firstChild) {
            event.preventDefault();
          }
        } else if (event.key === 'ArrowRight' && position === 'after') {
          // 如果光标在after区域的最右侧，阻止继续向右移动
          const lastChild = afterEl.lastChild;
          const isAtEnd =
            lastChild &&
            range.startContainer === lastChild &&
            range.startOffset === lastChild.textContent.length;
          if (isAtEnd) {
            event.preventDefault();
          }
        } else if (event.key === 'ArrowRight' && position === 'before') {
          // 如果光标在before区域的最右侧，移动到after区域的开始
          const lastChild = beforeEl.lastChild;
          if (
            lastChild &&
            range.startContainer === lastChild &&
            range.startOffset === lastChild.textContent.length
          ) {
            event.preventDefault();
            this.setCursorPosition(afterEl, 0);
          }
        } else if (event.key === 'ArrowLeft' && position === 'after') {
          // 如果光标在after区域的最左侧，移动到before区域的结束
          const isAtStart =
            !afterEl.firstChild ||
            (range.startContainer === afterEl.firstChild && range.startOffset === 0);
          if (isAtStart) {
            event.preventDefault();
            this.setCursorPosition(beforeEl, beforeEl.textContent.length);
          }
        }

        // 处理Backspace和Delete键
        if (event.key === 'Backspace' && position === 'before') {
          const isAtStart =
            !beforeEl.firstChild ||
            (range.startContainer === beforeEl.firstChild && range.startOffset === 0);
          if (isAtStart) {
            event.preventDefault();
          }
        } else if (event.key === 'Delete' && position === 'after') {
          const lastChild = afterEl.lastChild;
          const isAtEnd =
            lastChild &&
            range.startContainer === lastChild &&
            range.startOffset === lastChild.textContent.length;
          if (isAtEnd) {
            event.preventDefault();
          }
        }
      },
      enforceCharacterLimit(position) {
        // 只在编辑模式下执行长度限制
        if (!this.isEditMode || !this.$el) return;

        const beforeEl = this.$el.querySelector('.before-nickname');
        const afterEl = this.$el.querySelector('.after-nickname');

        // 安全检查
        if (!beforeEl || !afterEl) return;

        const beforeLength = beforeEl.textContent.length;
        const afterLength = afterEl.textContent.length;
        const totalLength = beforeLength + afterLength;

        // if (totalLength > this.maxLength) {
        //   const excess = totalLength - this.maxLength;

        //   if (position === 'before') {
        //     // 如果是修改前部分导致超长，截断前部分
        //     beforeEl.textContent = beforeEl.textContent.substring(0, beforeLength - excess);
        //   } else if (position === 'after') {
        //     // 如果是修改后部分导致超长，截断后部分
        //     afterEl.textContent = afterEl.textContent.substring(0, afterLength - excess);
        //   } else if (position === 'both') {
        //     // 初始化时如果超长，优先截断后半部分
        //     if (afterLength > 0) {
        //       const maxAfterLength = Math.max(0, this.maxLength - beforeLength);
        //       afterEl.textContent = afterEl.textContent.substring(0, maxAfterLength);
        //     }
        //     // 如果前半部分仍然超长，再截断前半部分
        //     if (beforeEl.textContent.length > this.maxLength) {
        //       beforeEl.textContent = beforeEl.textContent.substring(0, this.maxLength);
        //     }
        //   }

        //   // 保持光标位置
        //   if (position === 'before') {
        //     this.setCursorPosition(beforeEl, beforeEl.textContent.length);
        //   } else if (position === 'after') {
        //     this.setCursorPosition(afterEl, afterEl.textContent.length);
        //   }
        // }
      },
      setCursorPosition(element, offset) {
        if (!element) return;

        // 确保元素有内容
        if (!element.firstChild) {
          element.appendChild(document.createTextNode(''));
        }

        const selection = window.getSelection();
        const range = document.createRange();

        // 设置光标位置
        if (element.firstChild) {
          range.setStart(
            element.firstChild,
            Math.min(offset, element.firstChild.textContent.length)
          );
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      },
      saveMessage() {
        if (!this.canSave) return;

        // 获取前后文本内容
        const beforeText = this.$el.querySelector('.before-nickname').textContent;
        const afterText = this.$el.querySelector('.after-nickname').textContent;

        // 将用户昵称替换为模板变量 {user_nickname}
        const processedMessage = beforeText + '{user_nickname}' + afterText;

        // 触发保存事件，传递处理后的消息内容
        this.$emit('save', processedMessage);

        // 更新数据模型
        this.beforeText = beforeText;
        this.afterText = afterText;

        // 保存后退出编辑模式
        this.isEditMode = false;

        // 可以在这里添加保存成功的提示
        console.log('消息已保存:', processedMessage);
      }
    }
  };
</script>

<style scoped lang="less">
  .welcome-message-container {
    display: flex;
    align-items: center;
  }

  .message-display-wrapper {
    display: flex;
    align-items: center;
  }

  .message-display {
    font-size: 14px;
    line-height: 28px;
    min-height: 28px;
    padding: 4px 6px;
  }

  .display-nickname {
    color: #6287fd;
    font-weight: 500;
  }

  .edit-button {
    padding: 4px 12px;
    color: #0a7ff5;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    margin-left: 8px;
  }

  .edit-mode-container {
    display: flex;
    align-items: center;
  }

  .message-input-wrapper {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 4px 36px 4px 6px;
    position: relative;
    width: 260px;
  }

  .message-input-wrapper:focus-within {
    border-color: #409eff;
    outline: none;
  }

  .message-container {
    height: 28px;
    line-height: 28px;
    font-size: 14px;
    white-space: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .before-nickname,
  .after-nickname {
    display: inline-block;
    cursor: text;
    min-height: 28px;
    vertical-align: middle;
    outline: none;
    word-break: break-all;
    padding: 0 3px;
  }

  .before-nickname[contenteditable='true']:empty:before,
  .after-nickname[contenteditable='true']:empty:before {
    content: ' ';
    pointer-events: none;
  }

  .nickname-tag {
    display: inline-block;
    color: #6287fd;
    background: #ebf1ff;
    font-weight: 500;
    cursor: default;
    user-select: none;
    vertical-align: middle;
    white-space: nowrap;
    padding: 0 4px;
    font-size: 14px;
    border-radius: 4px;
  }

  .message-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
  }

  .char-count {
    font-size: 12px;
    color: #909399;
    position: absolute;
    right: 6px;
    bottom: 8px;
    &.error {
      color: #fb2626;
    }
  }

  .save-button {
    padding: 4px 12px;
    color: #0a7ff5;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  }

  .save-button:disabled {
    background-color: #c0c4cc;
    cursor: not-allowed;
  }

  /* 确保选择区域不会覆盖整个昵称标签 */
  .nickname-tag::selection {
    background: transparent;
  }

  .nickname-tag::-moz-selection {
    background: transparent;
  }
</style>
